import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { useEffect } from "react";
import { handleTrackedClick } from "./lib/analytics";
import MobileCallBar from "./components/MobileCallBar";
import TrustBar from "./components/TrustBar";
import Realisations from "./components/Realisations";

function ScrollToTop() {
  const { pathname, hash } = useLocation();
  useEffect(() => {
    if (!hash) {
      window.scrollTo(0, 0);
      return;
    }
    const t = setTimeout(() => document.getElementById(hash.slice(1))?.scrollIntoView(), 100);
    return () => clearTimeout(t);
  }, [pathname, hash]);
  useEffect(() => {
    document.addEventListener("click", handleTrackedClick, true);
    return () => document.removeEventListener("click", handleTrackedClick, true);
  }, []);
  return null;
}
import { Toaster } from "sonner";
import { LanguageProvider, useT } from "./lib/i18n";
import { useSEO } from "./lib/useSEO";
import Header from "./components/Header";
import Hero from "./components/Hero";
import About from "./components/About";
import Services from "./components/Services";
import Urgence from "./components/Urgence";
import Testimonials from "./components/Testimonials";
import ServiceArea from "./components/ServiceArea";
import ContactForm from "./components/ContactForm";
import FAQ from "./components/FAQ";
import Footer from "./components/Footer";
import WhatsAppButton from "./components/WhatsAppButton";
import BackToTop from "./components/BackToTop";
import CookieBanner from "./components/CookieBanner";
import AdminPage from "./pages/AdminPage";
import MentionsLegales from "./pages/MentionsLegales";
import PolitiqueConfidentialite from "./pages/PolitiqueConfidentialite";
import PaysagisteCreuse from "./pages/services/PaysagisteCreuse";
import EntretienJardinCreuse from "./pages/services/EntretienJardinCreuse";
import ElagageCreuse from "./pages/services/ElagageCreuse";
import DestructionFrelonsCreuse from "./pages/services/DestructionFrelonsCreuse";
import CityPage from "./pages/cities/CityPage";
import BlogIndex from "./pages/blog/BlogIndex";
import FrelonsAsiatiques from "./pages/blog/FrelonsAsiatiques";
import CreditImpotJardin from "./pages/blog/CreditImpotJardin";
import CalendrierTonteTaille from "./pages/blog/CalendrierTonteTaille";
import ReconnaitreNidFrelons from "./pages/blog/ReconnaitreNidFrelons";
import DeratisationCreuse from "./pages/blog/DeratisationCreuse";
import ChenillesProcessionnaires from "./pages/blog/ChenillesProcessionnaires";
import PunaisesDeLitGuide from "./pages/blog/PunaisesDeLitGuide";
import GuepeVsFrelon from "./pages/blog/GuepeVsFrelon";
import PrixInterventionNuisibles from "./pages/blog/PrixInterventionNuisibles";
import DynamicBlogPost from "./pages/blog/DynamicBlogPost";
import ZoneInterventionPage from "./pages/ZoneInterventionPage";
import EntrepriseNuisiblesCreuse from "./pages/EntrepriseNuisiblesCreuse";
import DesinsectisationCreuse from "./pages/DesinsectisationCreuse";
import DesinfectionCreuse from "./pages/DesinfectionCreuse";
import PunaisesDeLitCreuse from "./pages/PunaisesDeLitCreuse";
import TraitementVrillettesBoisCreuse from "./pages/TraitementVrillettesBoisCreuse";
import AntiNuisiblesGueret from "./pages/cities/AntiNuisiblesGueret";
import AntiNuisiblesAubusson from "./pages/cities/AntiNuisiblesAubusson";
import AntiNuisiblesLaSouterraine from "./pages/cities/AntiNuisiblesLaSouterraine";
import MeilleureEntrepriseNuisibles from "./pages/MeilleureEntrepriseNuisibles";
import PunaisesLitHotelsGites from "./pages/PunaisesLitHotelsGites";
import AProposKevin from "./pages/AProposKevin";
import ChenillesQuandComment from "./pages/blog/ChenillesQuandComment";
import TailleElagageAutomneHiver from "./pages/blog/TailleElagageAutomneHiver";
import NidFrelonsVideHiver from "./pages/blog/NidFrelonsVideHiver";
import RatsSourisAutomne from "./pages/blog/RatsSourisAutomne";
import PestCityPage from "./pages/cities/PestCityPage";
import { PEST_CITIES } from "./lib/pestCities";
import DeratisationCreusePage from "./pages/DeratisationCreusePage";
import CafardsBlattesCreuse from "./pages/CafardsBlattesCreuse";
import ChenillesProcessionnairesCreuse from "./pages/ChenillesProcessionnairesCreuse";

function HomePage() {
  const { lang } = useT();
  useSEO({
    title:
      lang === "en"
        ? "Landscaping & Pest Control in Creuse, France | K'REUSE"
        : "Paysagiste & anti-nuisibles en Creuse (23) | K'REUSE",
    description:
      lang === "en"
        ? "Landscaping (parks & gardens) and pest control in the Creuse department (France). Free quote, fast intervention, Certibiocide-approved."
        : "Création et entretien de parcs et jardins, lutte contre les nuisibles en Creuse (23). Devis gratuit, intervention rapide, entreprise agréée Certibiocide.",
    canonical: "https://kreuse-exterieur.fr/",
    hreflangs: {
      // FR is the canonical / default language — the homepage URL with no param.
      fr: "https://kreuse-exterieur.fr/",
      en: "https://kreuse-exterieur.fr/?lang=en",
      xDefault: "https://kreuse-exterieur.fr/",
    },
  });
  return (
    <div className="min-h-screen overflow-x-hidden">
      <Header />
      <main>
        <Hero />
        <TrustBar />
        <Urgence />
        <About />
        <Services />
        <Realisations />
        <ServiceArea />
        <FAQ />
        <ContactForm />
        <Testimonials />
      </main>
      <Footer />
      <WhatsAppButton />
      <BackToTop />
    </div>
  );
}

function App() {
  return (
    <div className="App">
      <LanguageProvider>
        <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<HomePage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/mentions-legales" element={<MentionsLegales />} />
          <Route path="/politique-confidentialite" element={<PolitiqueConfidentialite />} />
          <Route path="/paysagiste-creuse" element={<PaysagisteCreuse />} />
          <Route path="/entretien-jardin-creuse" element={<EntretienJardinCreuse />} />
          <Route path="/elagage-creuse" element={<ElagageCreuse />} />
          <Route path="/destruction-frelons-creuse" element={<DestructionFrelonsCreuse />} />
          <Route path="/location-materiel-creuse" element={<Navigate to="/" replace />} />
          <Route path="/paysagiste-gueret" element={<CityPage cityKey="gueret" />} />
          <Route path="/paysagiste-la-souterraine" element={<CityPage cityKey="la-souterraine" />} />
          <Route path="/paysagiste-aubusson" element={<CityPage cityKey="aubusson" />} />
          <Route path="/paysagiste-bourganeuf" element={<CityPage cityKey="bourganeuf" />} />
          <Route path="/paysagiste-boussac" element={<CityPage cityKey="boussac" />} />
          <Route path="/paysagiste-la-celle-dunoise" element={<CityPage cityKey="la-celle-dunoise" />} />
          <Route path="/conseils" element={<BlogIndex />} />
          <Route path="/conseils/frelons-asiatiques-creuse-que-faire" element={<FrelonsAsiatiques />} />
          <Route path="/conseils/credit-impot-50-entretien-jardin" element={<CreditImpotJardin />} />
          <Route path="/conseils/calendrier-tonte-taille-saisonniere" element={<CalendrierTonteTaille />} />
          <Route path="/conseils/reconnaitre-nid-frelons-asiatiques-creuse" element={<ReconnaitreNidFrelons />} />
          <Route path="/conseils/deratisation-creuse-quand-faire-appel-professionnel" element={<DeratisationCreuse />} />
          <Route path="/conseils/chenilles-processionnaires-danger-traitement" element={<ChenillesProcessionnaires />} />
          <Route path="/conseils/punaises-de-lit-reconnaitre-traiter-eviter" element={<PunaisesDeLitGuide />} />
          <Route path="/conseils/difference-guepe-frelon-frelon-asiatique" element={<GuepeVsFrelon />} />
          <Route path="/conseils/prix-intervention-anti-nuisibles-creuse" element={<PrixInterventionNuisibles />} />
          <Route path="/conseils/chenilles-processionnaires-quand-comment-s-en-debarrasser-creuse" element={<ChenillesQuandComment />} />
          <Route path="/conseils/taille-elagage-automne-hiver-bon-moment" element={<TailleElagageAutomneHiver />} />
          <Route path="/conseils/nid-frelons-asiatiques-vide-hiver-que-faire" element={<NidFrelonsVideHiver />} />
          <Route path="/conseils/rats-souris-automne-signes-infestation-prevention" element={<RatsSourisAutomne />} />
          <Route path="/conseils/:slug" element={<DynamicBlogPost />} />
          <Route path="/zone-intervention-creuse-et-limitrophes" element={<ZoneInterventionPage />} />
          <Route path="/entreprise-nuisibles-creuse" element={<EntrepriseNuisiblesCreuse />} />
          <Route path="/desinsectisation-creuse" element={<DesinsectisationCreuse />} />
          <Route path="/desinfection-creuse" element={<DesinfectionCreuse />} />
          <Route path="/punaises-de-lit-creuse" element={<PunaisesDeLitCreuse />} />
          <Route path="/traitement-vrillettes-bois-creuse" element={<TraitementVrillettesBoisCreuse />} />
          <Route path="/deratisation-creuse" element={<DeratisationCreusePage />} />
          <Route path="/cafards-blattes-creuse" element={<CafardsBlattesCreuse />} />
          <Route path="/chenilles-processionnaires-creuse" element={<ChenillesProcessionnairesCreuse />} />
          <Route path="/anti-nuisibles-gueret" element={<AntiNuisiblesGueret />} />
          <Route path="/anti-nuisibles-aubusson" element={<AntiNuisiblesAubusson />} />
          <Route path="/anti-nuisibles-la-souterraine" element={<AntiNuisiblesLaSouterraine />} />
          {PEST_CITIES.map((c) => (
            <Route key={c.slug} path={`/${c.slug}`} element={<PestCityPage city={c} />} />
          ))}
          <Route path="/meilleure-entreprise-anti-nuisibles-creuse" element={<MeilleureEntrepriseNuisibles />} />
          <Route path="/punaises-de-lit-hotels-gites-creuse" element={<PunaisesLitHotelsGites />} />
          <Route path="/a-propos-kevin-fondateur" element={<AProposKevin />} />
        </Routes>
        <MobileCallBar />
        <CookieBanner />
        <Toaster
          position="top-center"
          toastOptions={{
            style: {
              background: "#FFFFFF",
              color: "#1A2E1C",
              border: "1px solid #E5EBE5",
              borderRadius: "12px",
            },
          }}
        />
      </BrowserRouter>
      </LanguageProvider>
    </div>
  );
}

export default App;
